import { useState } from 'react';
import { BookOpen, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePlayerEnrollments, useCreateEnrollment, useRemoveEnrollment, type SessionType, type PlayerEnrollment } from '@/hooks/use-enrollments';
import { useTerms, type Term } from '@/hooks/use-terms';
import type { Id } from '../../../../convex/_generated/dataModel';
import { formatDate } from '@/lib/format';

const SESSION_TYPES: Array<{ value: SessionType; label: string }> = [
  { value: 'group',         label: 'Group' },
  { value: 'private',       label: 'Private' },
  { value: 'clinic',        label: 'Clinic' },
  { value: 'match_play',    label: 'Match Play' },
  { value: 'cardio_tennis', label: 'Cardio Tennis' },
  { value: 'squads',        label: 'Squads' },
];

const SESSION_TYPE_COLORS: Record<SessionType, string> = {
  group:         'bg-blue-100 text-blue-700',
  private:       'bg-purple-100 text-purple-700',
  clinic:        'bg-amber-100 text-amber-700',
  match_play:    'bg-red-100 text-red-700',
  cardio_tennis: 'bg-green-100 text-green-700',
  squads:        'bg-orange-100 text-orange-700',
};

interface EnrollmentsSectionProps {
  playerId: Id<'players'>;
}

export function EnrollmentsSection({ playerId }: EnrollmentsSectionProps) {
  const { data: enrollments, isLoading: enrollmentsLoading } = usePlayerEnrollments(playerId);
  const { data: terms, isLoading: termsLoading } = useTerms();
  const createEnrollment = useCreateEnrollment();
  const removeEnrollment = useRemoveEnrollment();

  const [showForm, setShowForm] = useState(false);
  const [selectedTermId, setSelectedTermId] = useState('');
  const [selectedSessionType, setSelectedSessionType] = useState<SessionType | ''>('');
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<Id<'enrollments'> | null>(null);

  const handleAdd = async () => {
    if (!selectedTermId) { setFormError('Please select a term.'); return; }
    if (!selectedSessionType) { setFormError('Please select a session type.'); return; }

    setFormError(null);
    try {
      await createEnrollment.mutateAsync({
        playerId,
        termId: selectedTermId as Id<'terms'>,
        sessionType: selectedSessionType,
      });
      setSelectedTermId('');
      setSelectedSessionType('');
      setShowForm(false);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to add enrollment.');
    }
  };

  const handleDelete = async (enrollmentId: Id<'enrollments'>) => {
    try {
      await removeEnrollment.mutateAsync(enrollmentId);
      setConfirmDeleteId(null);
    } catch {
      // silently handle
    }
  };

  // Group enrollments by term for display
  const byTerm: Record<string, { termName: string; termDates: string; items: PlayerEnrollment[] }> = {};
  for (const e of (enrollments ?? []) as PlayerEnrollment[]) {
    if (!byTerm[e.termId]) {
      byTerm[e.termId] = {
        termName: e.termName,
        termDates: `${formatDate(e.termStartDate)} – ${formatDate(e.termEndDate)}`,
        items: [],
      };
    }
    byTerm[e.termId].items!.push(e);
  }

  // Filter to session types not yet enrolled in the selected term
  const enrolledTypesForSelectedTerm = new Set(
    (enrollments ?? [])
      .filter((e: PlayerEnrollment) => e.termId === selectedTermId)
      .map((e: PlayerEnrollment) => e.sessionType)
  );
  const availableSessionTypes = SESSION_TYPES.filter(
    (st) => !enrolledTypesForSelectedTerm.has(st.value)
  );

  return (
    <div className="bg-card rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-muted-foreground" />
          <h2 className="font-semibold">Enrollments</h2>
          <span className="text-sm text-muted-foreground">
            ({enrollments?.length ?? 0})
          </span>
        </div>
        {!showForm && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowForm(true)}
            className="gap-1.5"
          >
            <Plus className="w-4 h-4" />
            Add
          </Button>
        )}
      </div>

      {/* Enrollment list grouped by term */}
      {(enrollmentsLoading || termsLoading) ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : Object.keys(byTerm).length === 0 ? (
        <p className="text-sm text-muted-foreground">No enrollments yet.</p>
      ) : (
        <div className="space-y-4">
          {Object.entries(byTerm).map(([termId, group]) => (
            <div key={termId}>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {group.termName}
                <span className="font-normal normal-case ml-1.5 text-gray-400">
                  {group.termDates}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {(group.items ?? []).map((e) => (
                  <div key={e.id} className="flex items-center gap-1">
                    <Badge
                      className={`text-xs font-medium ${SESSION_TYPE_COLORS[e.sessionType as SessionType] ?? 'bg-gray-100 text-gray-700'}`}
                    >
                      {SESSION_TYPES.find((s) => s.value === e.sessionType)?.label ?? e.sessionType}
                    </Badge>
                    {confirmDeleteId === e.id ? (
                      <div className="flex items-center gap-1 ml-1">
                        <AlertTriangle className="w-3 h-3 text-amber-500" />
                        <button
                          onClick={() => handleDelete(e.id)}
                          className="text-xs text-red-600 hover:underline"
                          disabled={removeEnrollment.isPending}
                        >
                          Remove
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-xs text-gray-400 hover:underline"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteId(e.id)}
                        className="text-gray-300 hover:text-red-400 transition-colors"
                        aria-label="Remove enrollment"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add enrollment form */}
      {showForm && (
        <div className="mt-4 border rounded-lg p-4 space-y-3 bg-gray-50">
          <p className="text-sm font-medium text-gray-700">Add Enrollment</p>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">Term</label>
            {!terms || terms.length === 0 ? (
              <p className="text-xs text-amber-600">
                No terms configured. Add a term in Settings first.
              </p>
            ) : (
              <select
                value={selectedTermId}
                onChange={(e) => setSelectedTermId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white"
              >
                <option value="">Select a term…</option>
                {terms.map((t: Term) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({formatDate(t.startDate)} – {formatDate(t.endDate)})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">Session Type</label>
            <div className="flex flex-wrap gap-2">
              {(selectedTermId ? availableSessionTypes : SESSION_TYPES).map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSelectedSessionType(value)}
                  className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                    selectedSessionType === value
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
              {selectedTermId && availableSessionTypes.length === 0 && (
                <p className="text-xs text-gray-500">
                  Player is already enrolled in all session types for this term.
                </p>
              )}
            </div>
          </div>

          {formError && (
            <p className="text-xs text-red-500">{formError}</p>
          )}

          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setShowForm(false); setFormError(null); }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={createEnrollment.isPending}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {createEnrollment.isPending ? 'Adding…' : 'Add Enrollment'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
